import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Title, Text, Card, Button } from '@tremor/react'
import { FlagIcon, PlusIcon, TrashIcon, PencilIcon, SparklesIcon } from '@heroicons/react/24/outline'
import { getResearchGoals, createResearchGoal, updateResearchGoal, deleteResearchGoal, generateGoalAISuggestions, acceptGoalSuggestion } from '../../Requests/ResearchGoalRequests'
import { sanitizeUserInput } from '../../utils/security'
import { useMyContext } from '../ContextProviders/CreditsContextProvider'
import UpgradedLoader from '../UpgradedLoader'
import ConfirmationModal from '../ConfirmationModal'
import GoalFormModal from './GoalFormModal'
import GoalAIGenerationModal from './GoalAIGenerationModal'

function ResearchGoalsTab({ projectUuid, onNavigateNext }) {
    const { t } = useTranslation()
    const { setCredits } = useMyContext()
    
    // Goals state
    const [goals, setGoals] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    // UI state
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [goalToDelete, setGoalToDelete] = useState(null)
    const [showCreateModal, setShowCreateModal] = useState(false)
    const [showEditModal, setShowEditModal] = useState(false)
    const [goalToEdit, setGoalToEdit] = useState(null)
    const [modalLoading, setModalLoading] = useState(false)
    const [showAIModal, setShowAIModal] = useState(false)
    const [aiLoading, setAILoading] = useState(false)

    const loadGoals = useCallback(async () => {
        try {
            setLoading(true)
            setError(null)
            const data = await getResearchGoals(projectUuid)
            setGoals(data.data || [])
        } catch (err) {
            console.error('Failed to load research goals:', err)
            setError(t('Research.ObjectivosHipoteses.Goals.LoadError'))
        } finally {
            setLoading(false)
        }
    }, [projectUuid, t])

    useEffect(() => {
        loadGoals()
    }, [loadGoals])

    const handleDeleteClick = (goalUuid) => {
        setGoalToDelete(goalUuid)
        setShowDeleteModal(true)
    }

    const handleDeleteConfirm = async () => {
        if (!goalToDelete) return

        try {
            await deleteResearchGoal(projectUuid, goalToDelete)
            setGoals(prev => prev.filter(g => g.uuid !== goalToDelete))
            setShowDeleteModal(false)
            setGoalToDelete(null)
        } catch (err) {
            console.error('Failed to delete research goal:', err)
            alert(err.error || t('Research.ObjectivosHipoteses.Goals.DeleteError'))
        }
    }

    const handleCreateManual = () => {
        setShowCreateModal(true)
    }

    const handleGenerateAI = () => {
        setShowAIModal(true)
    }

    const handleAIGenerationSubmit = async (responses, scope) => {
        setAILoading(true)
        try {
            const response = await generateGoalAISuggestions(projectUuid, scope, responses)
            
            if (response.data) {
                // AI suggestions generated successfully - now shows multiple goals
                console.log(`Generated ${response.data.length} AI research goal suggestions`)
                
                // Add all generated goals to the state
                if (Array.isArray(response.data)) {
                    setGoals(prev => [...prev, ...response.data])
                } else {
                    // Fallback for single goal (backward compatibility)
                    setGoals(prev => [...prev, response.data])
                }
                
                // Update credits if available
                if (response.total_credits_used) {
                    // Credits are handled by the backend response
                    console.log(`Used ${response.total_credits_used} credits for AI generation`)
                }
                
                setShowAIModal(false)
            }
        } catch (err) {
            console.error('Failed to generate AI goal suggestions:', err)
            throw err
        } finally {
            setAILoading(false)
        }
    }

    const handleEditGoal = (goalUuid) => {
        const goal = goals.find(g => g.uuid === goalUuid)
        if (goal) {
            setGoalToEdit(goal)
            setShowEditModal(true)
        }
    }

    const handleCreateSubmit = async (goalData) => {
        setModalLoading(true)
        try {
            const response = await createResearchGoal(projectUuid, goalData)
            if (response.data) {
                setGoals(prev => [...prev, response.data])
                setShowCreateModal(false)
                
                // Navigate to research hypotheses after creating a research goal
                if (onNavigateNext) {
                    setTimeout(() => onNavigateNext(), 500) // Small delay for UX
                }
            }
        } catch (err) {
            console.error('Failed to create research goal:', err)
            throw err
        } finally {
            setModalLoading(false)
        }
    }

    const handleEditSubmit = async (goalData) => {
        if (!goalToEdit) return
        
        setModalLoading(true)
        try {
            const response = await updateResearchGoal(projectUuid, goalToEdit.uuid, goalData)
            if (response.data) {
                setGoals(prev => prev.map(g => 
                    g.uuid === goalToEdit.uuid ? response.data : g
                ))
                setShowEditModal(false)
                setGoalToEdit(null)
            }
        } catch (err) {
            console.error('Failed to update research goal:', err)
            throw err
        } finally {
            setModalLoading(false)
        }
    }

    const handleStatusChange = async (goalUuid, newStatus) => {
        const goal = goals.find(g => g.uuid === goalUuid)
        if (!goal || goal.status === newStatus) return

        try {
            // Optimistic update
            setGoals(prev => prev.map(g => 
                g.uuid === goalUuid ? { ...g, status: newStatus } : g
            ))

            // Update on server
            const response = await updateResearchGoal(projectUuid, goalUuid, {
                general_goal: goal.general_goal,
                specific_goals: goal.specific_goals,
                reasoning: goal.reasoning,
                status: newStatus
            })
            
            if (response.data) {
                // Update with server response to ensure consistency
                setGoals(prev => prev.map(g => 
                    g.uuid === goalUuid ? response.data : g
                ))
            }
        } catch (err) {
            // Revert optimistic update on error
            setGoals(prev => prev.map(g => 
                g.uuid === goalUuid ? goal : g
            ))
            console.error('Failed to update research goal status:', err)
            alert(err.error || t('Research.ObjectivosHipoteses.Goals.StatusUpdateError'))
        }
    }

    const handleSelectGoal = async (goalUuid) => {
        try {
            // First deactivate all other goals
            const updatePromises = goals.map(g => {
                if (g.uuid === goalUuid) {
                    return updateResearchGoal(projectUuid, g.uuid, {
                        general_goal: g.general_goal,
                        specific_goals: g.specific_goals,
                        reasoning: g.reasoning,
                        status: 'active'
                    })
                } else if (g.status === 'active') {
                    return updateResearchGoal(projectUuid, g.uuid, {
                        general_goal: g.general_goal,
                        specific_goals: g.specific_goals,
                        reasoning: g.reasoning,
                        status: 'draft'
                    })
                }
                return Promise.resolve()
            })

            await Promise.all(updatePromises)
            
            // Update local state immediately
            setGoals(prev => 
                prev.map(g => ({
                    ...g,
                    status: g.uuid === goalUuid ? 'active' : (g.status === 'active' ? 'draft' : g.status)
                }))
            )
            
            // Navigate to next step after selecting a goal
            if (onNavigateNext) {
                setTimeout(() => onNavigateNext(), 500) // Small delay for UX
            }
        } catch (err) {
            console.error('Failed to select research goal:', err)
            alert(err.error || t('Research.ObjectivosHipoteses.Goals.SelectError'))
        }
    }


    if (loading) {
        return (
            <div className="flex justify-center items-center py-12">
                <UpgradedLoader className="animate-spin" w={8} h={8} bg="bg-iavisus-100" />
            </div>
        )
    }

    return (
        <div data-testid="research-goals-container" className="space-y-6">
            <div>
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                        <FlagIcon className="h-6 w-6 text-iavisus-100" />
                        <Title className="text-lg font-semibold">
                            {t('Research.ObjectivosHipoteses.Goals.Title')}
                        </Title>
                    </div>
                    <div className="flex space-x-2">
                        <Button
                            data-testid="research-goals-create-btn"
                            size="sm"
                            icon={PlusIcon}
                            onClick={handleCreateManual}
                            className="bg-white border border-iavisus-100 text-iavisus-100 hover:bg-iavisus-50"
                        >
                            {t('Research.ObjectivosHipoteses.Goals.CreateManual')}
                        </Button>
                        <Button
                            data-testid="research-goals-ai-btn"
                            size="sm"
                            icon={SparklesIcon}
                            onClick={handleGenerateAI}
                            className="bg-iavisus-100 text-white hover:bg-iavisus-200"
                        >
                            {t('Research.ObjectivosHipoteses.Goals.GenerateAI')}
                        </Button>
                    </div>
                </div>
                
                <Text className="text-gray-600 mb-6">
                    {t('Research.ObjectivosHipoteses.Goals.Description')}
                </Text>

                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                        <Text className="text-red-600">{error}</Text>
                    </div>
                )}

                {goals.length === 0 ? (
                    <div data-testid="research-goals-empty" className="bg-gray-50 rounded-lg p-8 text-center">
                        <FlagIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <Text className="text-gray-500 mb-2">
                            {t('Research.ObjectivosHipoteses.Goals.Description')}
                        </Text>
                        <Text className="text-gray-400 text-sm">
                            Create your first research goal to get started.
                        </Text>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {goals.map((goal) => (
                            <Card key={goal.uuid} data-testid={`research-goals-item-${goal.uuid}`} className="p-4">
                                <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                        <div className="flex items-center space-x-2 mb-2">
                                            <span className={`inline-block w-2 h-2 rounded-full ${
                                                goal.type === 'ai_generated' ? 'bg-iavisus-100' : 'bg-blue-500'
                                            }`}></span>
                                            <Text className="text-xs text-gray-500">
                                                {goal.type === 'ai_generated' ? 
                                                    t('Research.ObjectivosHipoteses.Goals.AIGenerated') : 
                                                    t('Research.ObjectivosHipoteses.Goals.ManualType')
                                                }
                                            </Text>
                                            {goal.status === 'active' && (
                                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-iavisus-900 text-white">
                                                    {t(`Research.ObjectivosHipoteses.Goals.Status.active`)}
                                                </span>
                                            )}
                                        </div>
                                        
                                        <div className="mb-3">
                                            <Text className="font-medium text-gray-900 mb-1">General Objective:</Text>
                                            <Text className="text-gray-700">
                                                {goal.general_goal || t('Research.ObjectivosHipoteses.Goals.EmptyGoal')}
                                            </Text>
                                        </div>

                                        {goal.specific_goals && goal.specific_goals.length > 0 && (
                                            <div className="mb-3">
                                                <Text className="font-medium text-gray-900 mb-1">Specific Objectives:</Text>
                                                <ul className="list-disc list-inside space-y-1">
                                                    {goal.specific_goals.map((specific, index) => (
                                                        <li key={index} className="text-gray-700 text-sm">
                                                            {specific}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}

                                        {goal.reasoning && (
                                            <details className="mb-2">
                                                <summary className="text-sm text-gray-600 cursor-pointer hover:text-gray-800">
                                                    {t('Research.ObjectivosHipoteses.Goals.ShowReasoning')}
                                                </summary>
                                                <Text className="text-sm text-gray-600 mt-2 pl-4 border-l-2 border-gray-200">
                                                    {goal.reasoning}
                                                </Text>
                                            </details>
                                        )}
                                        
                                        <div className="flex items-center space-x-4 text-xs text-gray-500">
                                            <span>{t('Research.ObjectivosHipoteses.Goals.CreatedBy')} {goal.user?.name || t('Common.Unknown')}</span>
                                            <span>{new Date(goal.created_at).toLocaleDateString()}</span>
                                            {goal.credits_used > 0 && (
                                                <span>{goal.credits_used} {t('Common.Credits')}</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex space-x-2 ml-4">
                                        {goal.status !== 'active' && (
                                            <button
                                                type="button"
                                                onClick={() => handleSelectGoal(goal.uuid)}
                                                className="px-3 py-1 text-sm bg-iavisus-100 text-white rounded-md hover:bg-iavisus-200 transition-colors"
                                                title={t('Research.ObjectivosHipoteses.Goals.ClickToSelect')}
                                            >
                                                {t('Research.ObjectivosHipoteses.Goals.Select')}
                                            </button>
                                        )}
                                        <button
                                            onClick={() => handleEditGoal(goal.uuid)}
                                            className="p-1 text-gray-500 hover:text-gray-700"
                                            title={t('Common.Edit')}
                                        >
                                            <PencilIcon className="h-4 w-4" />
                                        </button>
                                        <button
                                            onClick={() => handleDeleteClick(goal.uuid)}
                                            className="p-1 text-red-500 hover:text-red-700"
                                            title={t('Common.Delete')}
                                        >
                                            <TrashIcon className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                )}
            </div>

            {/* Create Goal Modal */}
            <GoalFormModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onSubmit={handleCreateSubmit}
                loading={modalLoading}
            />

            {/* Edit Goal Modal */}
            <GoalFormModal
                isOpen={showEditModal}
                onClose={() => {
                    setShowEditModal(false)
                    setGoalToEdit(null)
                }}
                onSubmit={handleEditSubmit}
                editGoal={goalToEdit}
                loading={modalLoading}
            />

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                open={showDeleteModal}
                setOpen={setShowDeleteModal}
                title={t('Common.Delete')}
                message={t('Research.ObjectivosHipoteses.Goals.DeleteConfirm')}
                confirmText={t('Common.Delete')}
                cancelText={t('Common.Cancel')}
                variant="danger"
                onConfirm={handleDeleteConfirm}
            />

            {/* AI Goal Generation Modal */}
            <GoalAIGenerationModal
                isOpen={showAIModal}
                onClose={() => setShowAIModal(false)}
                onSubmit={handleAIGenerationSubmit}
                loading={aiLoading}
            />
        </div>
    )
}

export default ResearchGoalsTab